import { lerp } from '../core/math';
import { tuning } from '../sim/tuning';

export type CameraState = {
  x: number;
  y: number;
  zoom: number;
  shakeX: number;
  shakeY: number;
  shake: number;
  unframed?: boolean;
};

export function createCamera(bounds: { x: number; y: number; w: number; h: number }): CameraState {
  return {
    x: bounds.x + bounds.w / 2,
    y: bounds.y + bounds.h / 2,
    zoom: 1,
    shakeX: 0,
    shakeY: 0,
    shake: 0,
    unframed: true,
  };
}

export function updateCamera(
  cam: CameraState,
  targets: { x: number; y: number }[],
  bounds: { x: number; y: number; w: number; h: number },
  viewW: number,
  viewH: number,
  dtSeconds = 1 / 60,
): CameraState {
  const pts = targets.length
    ? targets
    : [{ x: bounds.x + bounds.w / 2, y: bounds.y + bounds.h / 2 }];
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of pts) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const pad = tuning.cameraPadding;
  minX -= pad;
  maxX += pad;
  minY -= pad;
  maxY += pad;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const spanX = Math.max(maxX - minX, 8);
  const spanY = Math.max(maxY - minY, 6);
  const ppmFit = Math.min(viewW / spanX, viewH / spanY);
  const ppmArena = Math.min(viewW / bounds.w, viewH / bounds.h);
  const zoom = Math.min(ppmFit, ppmArena * 1.8);
  // A fresh match starts at a readable scale; smoothing applies to subsequent movement.
  if (cam.unframed) {
    cam.x = cx;
    cam.y = cy;
    cam.zoom = zoom;
    cam.unframed = false;
  }
  // Match the original 60 Hz response at every refresh rate.
  const frames = Math.max(0, dtSeconds) * 60;
  const follow = 1 - Math.pow(1 - tuning.cameraLerp, frames);
  // A small world-space dead zone stops idle limbs and ordinary footwork from moving the scene.
  const outside = (delta: number, deadZone: number) => Math.sign(delta) * Math.max(0, Math.abs(delta) - deadZone);
  cam.x += outside(cx - cam.x, bounds.w * 0.012) * follow;
  cam.y += outside(cy - cam.y, bounds.h * 0.025) * follow;
  // Open immediately to protect the outer fighters; close gently after the action gathers again.
  const zoomFollow = 1 - Math.pow(1 - tuning.cameraZoomLerp * 0.45, frames);
  cam.zoom = zoom < cam.zoom ? zoom : lerp(cam.zoom, zoom, zoomFollow);
  // Even while the centre catches up (or the viewport resizes), bodies stay inside the frame.
  const safeX = Math.max(Math.abs(maxX - cam.x), Math.abs(minX - cam.x));
  const safeY = Math.max(Math.abs(maxY - cam.y), Math.abs(minY - cam.y));
  cam.zoom = Math.min(cam.zoom, viewW / (safeX * 2), viewH / (safeY * 2));
  cam.shake *= Math.pow(0.85, frames);
  cam.shakeX = (Math.random() - 0.5) * cam.shake;
  cam.shakeY = (Math.random() - 0.5) * cam.shake;
  return cam;
}

export function addShake(cam: CameraState, amount: number): void {
  cam.shake = Math.min(24, cam.shake + amount);
}

export function worldToScreen(
  cam: CameraState,
  x: number,
  y: number,
  viewW: number,
  viewH: number,
): { x: number; y: number } {
  const ppm = cam.zoom;
  return {
    x: (x - cam.x) * ppm + viewW / 2 + cam.shakeX,
    y: viewH / 2 - (y - cam.y) * ppm + cam.shakeY,
  };
}
